import React, { useMemo, useState, useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import type { Layout, Layouts } from 'react-grid-layout';
import { DashboardToolbar } from '../components/DashboardToolbar';
import { ResizeHandle } from '../components/ResizeHandle';
import { PermissionGate } from '../components/PermissionGate';
import { SqueezoWidget } from '../components/SqueezoWidget';
import { SteamroomLevelsWidget } from '../components/SteamroomLevelsWidget';
import { WiseguyAlertsWidget } from '../components/WiseguyAlertsWidget';
import { UpgradesDowngradesWidget } from '../components/UpgradesDowngradesWidget';
import { CongressTradesWidget } from '../components/CongressTradesWidget';
import { InsiderTradesWidget } from '../components/InsiderTradesWidget';
import { MarketMoversWidget } from '../components/MarketMoversWidget';
import { TDSequentialWidget } from '../components/TDSequentialWidget';
import { NinerBreadthWidget } from '../components/NinerBreadthWidget';
import { GexProfileWidget } from '../components/GexProfileWidget';
import { GexRayWidget } from '../components/GexRayWidget';
import { DealerGexHeatmapWidget } from '../components/DealerGexHeatmapWidget';
import { CandleChartWidget } from '../components/candle-chart/CandleChartWidget';
import { ZerodayBreadthWidget } from '../components/zeroday-breadth/ZerodayBreadthWidget';
import { CumulativePremiumV2Widget } from '../components/CumulativePremiumV2Widget';
import { EconomicCalendarWidget } from '../components/EconomicCalendarWidget';
import { FJVoiceWidget } from '../components/FJVoiceWidget';
import { TickstrikeWidget } from '../components/TickstrikeWidget';
import { FJHeadlinesWidget } from '../components/FJHeadlinesWidget';
import { DiscordComposerWidget } from '../components/DiscordComposerWidget';
import { SentimentControlWidget } from '../components/SentimentControlWidget';
import { WsjFeedWidget } from '../components/WsjFeedWidget';
import { MigrationDiscordBanner } from '../components/MigrationDiscordBanner';
import { OnboardingTour } from '../components/OnboardingTour';
import { useDashboard } from '../contexts/DashboardContext';
import type { ToolWindow } from '../types/dashboard';
import 'react-grid-layout/css/styles.css';

// Create responsive grid layout with automatic width detection
const ResponsiveGridLayout = WidthProvider(Responsive);

// Breakpoints based on container width (not viewport)
const BREAKPOINTS = { lg: 1200, md: 768, sm: 480, xs: 0 };
const COLS = { lg: 12, md: 6, sm: 4, xs: 2 };

// Widget component renderer
const renderWidget = (window: ToolWindow, onRemove: (id: string) => void) => {
  const content = (() => {
    switch (window.type) {
      case 'wiseguy-alerts':
        return <WiseguyAlertsWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'squeezo':
        return <SqueezoWidget onRemove={() => onRemove(window.id)} />;

      case 'steamroom-levels':
        return <SteamroomLevelsWidget onRemove={() => onRemove(window.id)} />;

      case 'upgrades-downgrades':
        return <UpgradesDowngradesWidget onRemove={() => onRemove(window.id)} />;

      case 'congress-trades':
        return <CongressTradesWidget onRemove={() => onRemove(window.id)} />;

      case 'insider-trades':
        return <InsiderTradesWidget onRemove={() => onRemove(window.id)} />;

      case 'market-movers':
        return <MarketMoversWidget onRemove={() => onRemove(window.id)} />;

      case 'td-sequential':
        return <TDSequentialWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'niner-breadth':
        return <NinerBreadthWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'gex-profile':
        return <GexProfileWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'gex-ray':
        return <GexRayWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'gex-heatmap':
        return <DealerGexHeatmapWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'nq-scalper':
        return <CandleChartWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'zeroday-breadth':
        return <ZerodayBreadthWidget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'cumulative-premium':
        return <CumulativePremiumV2Widget windowId={window.id} onRemove={() => onRemove(window.id)} />;

      case 'fj-economic-calendar':
        return <EconomicCalendarWidget onRemove={() => onRemove(window.id)} />;

      case 'fj-voice':
        return <FJVoiceWidget onRemove={() => onRemove(window.id)} />;

      case 'fj-tickstrike':
        return <TickstrikeWidget onRemove={() => onRemove(window.id)} />;

      case 'fj-headlines':
        return <FJHeadlinesWidget onRemove={() => onRemove(window.id)} />;

      case 'discord-composer':
        return <DiscordComposerWidget onRemove={() => onRemove(window.id)} />;

      case 'sentiment-control':
        return <SentimentControlWidget onRemove={() => onRemove(window.id)} />;

      case 'wsj-feed':
        return <WsjFeedWidget onRemove={() => onRemove(window.id)} />;

      default:
        return (
          <div className="bg-dark-surface rounded-lg border border-dark-border h-full overflow-hidden flex flex-col">
            {/* Title Bar - drag handle only */}
            <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-dark-surface-light border-b border-dark-border cursor-move">
              <h3 className="text-sm font-semibold text-white">Unknown Widget</h3>
            </div>
            {/* Toolbar - NOT draggable */}
            <div className="flex items-center justify-end px-3 py-1 bg-dark-bg/50 border-b border-dark-border">
              <button
                onClick={() => onRemove(window.id)}
                className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors"
                title="Close widget"
              >
                ×
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <p>Unknown widget type: {window.type}</p>
            </div>
          </div>
        );
    }
  })();

  // Wrap content with PermissionGate
  return (
    <div key={window.id} className="h-full">
      <PermissionGate toolType={window.type} onRemove={() => onRemove(window.id)}>
        {content}
      </PermissionGate>
    </div>
  );
};

export const DashboardPage: React.FC = () => {
  const { currentDashboard, updateLayouts, removeWindow, isLoading } = useDashboard();

  const [tourRestartCount, setTourRestartCount] = useState(0);

  useEffect(() => {
    const handleRestart = () => setTourRestartCount((c) => c + 1);
    window.addEventListener('restart-tour', handleRestart);
    return () => window.removeEventListener('restart-tour', handleRestart);
  }, []);

  const layout = currentDashboard?.layout || [];
  const windows = currentDashboard?.windows || [];

  // Use per-breakpoint layouts if available, otherwise fall back to single layout
  const layouts: Layouts = useMemo(() => {
    if (currentDashboard?.layouts) {
      return currentDashboard.layouts;
    }
    // Backward compat: use single layout for lg only, let RGL generate others
    return { lg: layout };
  }, [currentDashboard?.layouts, layout]);

  // Save all breakpoint layouts on every change
  const handleLayoutChange = (_currentLayout: Layout[], allLayouts: Layouts) => {
    updateLayouts(allLayouts);
  };

  return (
    <div className="flex-1 min-h-0 bg-dark-bg flex flex-col">
      <DashboardToolbar />
      <MigrationDiscordBanner />

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-auto min-h-0" data-tour="dashboard-grid">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400">Loading dashboard...</div>
          </div>
        ) : windows.length === 0 ? (
          <div className="flex items-center justify-center min-h-[calc(100vh-10rem)] relative">
            {/* Watermark logo */}
            <img
              src="/logo-512.png"
              alt=""
              className="absolute opacity-5 w-64 h-64 object-contain pointer-events-none select-none"
            />
            <div className="text-center relative z-10">
              <p className="text-gray-400 mb-4">No windows on this dashboard</p>
              <p className="text-sm text-gray-500">
                Click "Add New Tool" in the toolbar to get started
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            rowHeight={30}
            isDraggable={true}
            isResizable={true}
            compactType="vertical"
            preventCollision={false}
            draggableHandle=".widget-drag-handle"
            draggableCancel=".no-drag"
            resizeHandle={<ResizeHandle />}
            onLayoutChange={handleLayoutChange}
            measureBeforeMount={false}
            useCSSTransforms={true}
          >
            {windows.map((window) => renderWidget(window, removeWindow))}
          </ResponsiveGridLayout>
        )}
      </main>
      <OnboardingTour triggerRestart={tourRestartCount} />
    </div>
  );
};
